import React, { useEffect, useState, useRef } from "react";
import { Button, Popover, Tabs } from "@mantine/core";
import { IconInfoCircle, IconPlus, IconTrash } from "@tabler/icons-react";
import type {
  DynamicGroupField,
  Field,
  GroupField,
  SchemaShape,
} from "../../../../types";
import { useTaxOrganizerStore } from "../../../../utils/stores/taxOrganizerStore";
import { validateField } from "../../../../utils/configs/formSchema";
import {
  renderCheckboxChoiceField,
  renderCheckboxField,
  renderChoiceField,
  renderDateField,
  renderDifferentDaycaresField,
  renderDynamicInputPairField,
  renderNumberField,
  renderPasswordField,
  renderPhoneNumberField,
  renderRevenueInputField,
  renderSelectField,
  renderTextField,
  renderTilesField,
} from "./FieldRenderers";
import { FilingStatusModal } from "../Custom/FilingStatusModal/FilingStatusModal";
import { useDisclosure } from "@mantine/hooks";
interface DynamicGroupProps {
  groupField: DynamicGroupField;
  disabled?: boolean; // Add the optional disabled prop
  initialGroupCount?: number;
  taxYear: number; // REQUIRED - tax year for organizer
}

/**
 * DynamicGroup component to render fields based on the dynamic group field configuration.
 *
 * @param {DynamicGroupProps} props - The props for the component, including the field configuration,and whether or not it is disabled for the review page
 * @returns {JSX.Element} The rendered JSX element.
 */
const DynamicGroup: React.FC<DynamicGroupProps> = ({
  groupField,
  disabled = false,
  initialGroupCount = 1,
  taxYear,
}) => {
  const {
    responses,
    updateResponses,
    validationErrors,
    setValidationErrors,
    activeTab,
    setActiveTab,
  } = useTaxOrganizerStore();

  const groups = responses[groupField.name] || [];
  const currentGroupCount =
    responses?.personalInfo?.[0]?.[groupField.initialGroupCountLabel] ??
    initialGroupCount;

  const [opened, setOpened] = useState(false);
  const [openPopover, { close, open }] = useDisclosure(false);
  const isSyncingRef = useRef(false);

  useEffect(() => {
    if (groupField?.useGroupCount && !isSyncingRef.current) {
      // Simple direct update based on currentGroupCount
      if (currentGroupCount !== groups.length) {
        isSyncingRef.current = true;

        // Use current values to avoid stale state
        const currentGroups = responses[groupField.name] || [];
        const updatedGroups = Array(currentGroupCount)
          .fill({})
          .map((_, i) => currentGroups?.[i] || {});

        // Only update if there's actually a change
        if (JSON.stringify(currentGroups) !== JSON.stringify(updatedGroups)) {
          updateResponses({
            ...responses,
            [groupField.name]: updatedGroups,
          });

          // Update active tab if needed
          if (parseInt(activeTab) >= currentGroupCount) {
            setActiveTab((currentGroupCount - 1).toString());
          }
        }

        // Reset the syncing flag after state update cycle completes
        setTimeout(() => {
          isSyncingRef.current = false;
        }, 0);
      } else {
        // Ensure flag is reset if condition is false
        isSyncingRef.current = false;
      }
    }
  }, [
    currentGroupCount,
    groups?.length,
    groupField?.useGroupCount,
    groupField?.name,
  ]);

  const addGroup = () => {
    const newGroup = {};
    const updatedGroups = [...(responses[groupField.name] || []), newGroup];
    updateResponses({
      ...responses,
      [groupField.name]: updatedGroups,
      personalInfo: [
        {
          ...responses.personalInfo?.[0],
          [groupField.initialGroupCountLabel]: updatedGroups.length,
        },
      ],
    });
  };

  const removeGroup = (index: number) => {
    if ((responses[groupField.name] || []).length > 1) {
      const updatedGroups = (responses[groupField.name] || []).filter(
        (_: any, i: number) => i !== index
      );
      updateResponses({
        ...responses,
        [groupField.name]: updatedGroups,
        personalInfo: [
          {
            ...responses.personalInfo?.[0],
            [groupField.initialGroupCountLabel]: updatedGroups.length,
          },
        ],
      });
    }
  };

  const getErrorMessage = (fieldName: string, groupIndex: number) => {
    const baseErrorKey = `${groupField.name}[${groupIndex}]`;

    // Find all relevant error keys
    const relevantKeys = Object.keys(validationErrors).filter(
      (key) => key.startsWith(baseErrorKey) && key.includes(fieldName)
    );
    if (relevantKeys.length > 0) {
      const messages = relevantKeys.map((key) => validationErrors[key]);
      // Extract the last field from the error message
      const errorMessages = messages.map((message) => {
        const lastField = message.split(".").pop();
        return lastField;
      });
      return errorMessages.join(", ");
    }

    return ""; // Return an empty string instead of null
  };

  // Helper function to check for dynamic matches
  const handleFieldChange = async (
    name: string,
    newValue: any,
    groupIndex: number,
    option?: any,
    field?: Field,
    isDeselecting?: boolean
  ) => {
    const updatedGroups = [...groups];
    updatedGroups[groupIndex] = {
      ...updatedGroups[groupIndex],
      [name]: newValue,
    };

    const choiceGroup = field?.optionFields?.[option]?.[0].fields;

    updateResponses({
      ...responses,
      [groupField.name]: updatedGroups,
    });

    if (isDeselecting) {
      // Clear out all fields in the choiceGroup
      if (choiceGroup) {
        const updatedValues = { ...updatedGroups[groupIndex] };
        choiceGroup.forEach((field: Field) => {
          delete updatedValues[field.name];
        });
        updatedGroups[groupIndex] = updatedValues;
      }
    }

    // Validate the field
    const allValues = updatedGroups[groupIndex];
    const error = await validateField(
      groupField.name as keyof SchemaShape,
      groupIndex,
      name,
      newValue,
      allValues
    );

    // Update the validation errors
    const newValidationErrors = { ...validationErrors };

    if (error) {
      newValidationErrors[`${groupField.name}[${groupIndex}].${name}`] = error;
    } else {
      delete newValidationErrors[`${groupField.name}[${groupIndex}].${name}`];
    }
    // Additional validation for linked fields (SSN and confirmSSN)
    if (name === "SSN" || name === "confirmSSN") {
      const linkedFieldName = name === "SSN" ? "confirmSSN" : "SSN";
      const linkedValue = allValues[linkedFieldName];

      // Revalidate linked field
      const linkedError = await validateField(
        groupField.name as keyof SchemaShape,
        groupIndex,
        linkedFieldName,
        linkedValue,
        allValues
      );

      if (linkedError) {
        newValidationErrors[
          `${groupField.name}[${groupIndex}].${linkedFieldName}`
        ] = linkedError;
      } else {
        delete newValidationErrors[
          `${groupField.name}[${groupIndex}].${linkedFieldName}`
        ];
      }
    }

    setValidationErrors(newValidationErrors);
  };

  const handleSingleSelect = (
    name: string,
    option: any,
    groupIndex: number
  ) => {
    handleFieldChange(name, option, groupIndex);
  };

  /**
   * Handles the multi-select behavior for a field.
   * Also if exclusive last option is true, when clicking on last option, it will deselect all other options
   *
   * @param {string} name - The name of the field.
   * @param {any} option - The option to select or deselect.
   * @param {number} groupIndex - The index of the group.
   * @param {Field} [field] - The field configuration (optional).
   */
  const handleMultiSelect = (
    name: string,
    option: any,
    groupIndex: number,
    field?: Field
  ) => {
    const currentValues = groups[groupIndex]?.[name] || [];
    const isDeselecting = currentValues.includes(option);
    let newValues: any[] = [];

    if (field?.exclusiveLastOption && field.options) {
      // Exclusive last option behavior
      const lastOptionIndex = field.options.length - 1;
      const lastOptionValue = field.options[lastOptionIndex]?.value;
      const isLastOption = option === lastOptionValue;
      const isOptionSelected = currentValues.includes(option);

      if (isLastOption) {
        // If last option is selected
        newValues = isOptionSelected ? [] : [option];
      } else {
        // Remove last option if it's selected
        newValues = currentValues.filter(
          (value: any) => value !== lastOptionValue
        );

        if (isOptionSelected) {
          // Deselect the clicked option
          newValues = newValues.filter((value: any) => value !== option);
        } else {
          // Select the clicked option
          newValues = [...newValues, option];
        }
      }
    } else {
      newValues = isDeselecting
        ? currentValues.filter((value: any) => value !== option)
        : [...currentValues, option];
    }

    handleFieldChange(
      name,
      newValues,
      groupIndex,
      option,
      field,
      isDeselecting
    );
  };

  const handleChange = (
    name: string,
    option: any,
    isMultiSelect: boolean,
    groupIndex: number,
    field?: Field
  ) => {
    isMultiSelect
      ? handleMultiSelect(name, option, groupIndex, field)
      : handleSingleSelect(name, option, groupIndex);
  };

  const renderFields = (fields: Field[], groupIndex: number) => {
    return fields.map((field: Field) => {
      const fieldValue = groups[groupIndex]?.[field.name] ?? "";
      const errorMessage = getErrorMessage(field.name, groupIndex);
      const modalField = field?.modalField;
      const fieldKey = field.id || `${field.name}-${groupIndex}`;

      const popoverContent = (
        <Popover
          width={320}
          withArrow
          position="bottom"
          shadow="md"
          opened={openPopover}
        >
          <Popover.Target>
            <Button
              className="info-button"
              onMouseEnter={() => open()}
              onMouseLeave={() => close()}
              style={{ height: "20px" }}
            >
              <IconInfoCircle color="#4D5357" />
            </Button>
          </Popover.Target>
          <Popover.Dropdown style={{ borderRadius: "0.5rem" }}>
            <div className="popover-content">
              <h3 style={{ textAlign: "center" }}>{field?.info?.title}</h3>
              {Array.isArray(field?.info?.description) ? (
                field.info.description.map(
                  (paragraph: { id: number; text: string }) => (
                    <p key={paragraph.id}>{paragraph.text}</p>
                  )
                )
              ) : (
                <p>{field?.info?.description}</p>
              )}
            </div>
          </Popover.Dropdown>
        </Popover>
      );

      switch (field.type) {
        case "textInput":
          return (
            <React.Fragment key={fieldKey}>
              {renderTextField(
                {
                  field,
                  value: fieldValue,
                  className: "",
                  popoverContent,
                },
                {
                  groupIndex,
                  handleFieldChange,
                  disabled,
                  errorMessage,
                }
              )}
            </React.Fragment>
          );
        case "dateInput":
          return (
            <React.Fragment key={fieldKey}>
              {renderDateField(
                field,
                groupIndex,
                fieldValue,
                handleFieldChange,
                disabled,
                errorMessage
              )}
            </React.Fragment>
          );
        case "passwordInput":
          return (
            <React.Fragment key={fieldKey}>
              {renderPasswordField(
                field,
                groupIndex,
                fieldValue,
                handleFieldChange,
                disabled,
                errorMessage
              )}
            </React.Fragment>
          );
        case "numberInput":
          return (
            <React.Fragment key={fieldKey}>
              {renderNumberField(
                field,
                groupIndex,
                fieldValue,
                handleFieldChange,
                disabled,
                errorMessage
              )}
            </React.Fragment>
          );
        case "phoneNumberInput":
          return (
            <React.Fragment key={fieldKey}>
              {renderPhoneNumberField(
                field,
                groupIndex,
                fieldValue,
                handleFieldChange,
                disabled,
                errorMessage
              )}
            </React.Fragment>
          );
        case "select":
          return (
            <React.Fragment key={fieldKey}>
              {renderSelectField(
                field,
                groupIndex,
                fieldValue,
                handleFieldChange,
                disabled,
                errorMessage
              )}
            </React.Fragment>
          );
        case "checkbox":
          return (
            <React.Fragment key={fieldKey}>
              {renderCheckboxField(
                field,
                groupIndex,
                fieldValue,
                handleFieldChange,
                disabled,
                errorMessage
              )}
            </React.Fragment>
          );
        case "checkbox-choice":
          return (
            <React.Fragment key={fieldKey}>
              {renderCheckboxChoiceField(
                field,
                groupIndex,
                fieldValue,
                handleChange,
                disabled,
                errorMessage
              )}
            </React.Fragment>
          );
        case "choice":
          return (
            <React.Fragment key={fieldKey}>
              {renderChoiceField(
                {
                  field,
                  value: fieldValue,
                  children: (
                    <Button
                      variant="transparent"
                      style={{ color: "#0077CC" }}
                      className="modal-field-label"
                      onClick={() => setOpened(true)}
                    >
                      {modalField}
                    </Button>
                  ),
                },
                {
                  groupIndex,
                  handleChange,
                  disabled,
                  errorMessage,
                  renderFields,
                }
              )}
            </React.Fragment>
          );
        case "tiles":
          return (
            <React.Fragment key={fieldKey}>
              {renderTilesField(
                field,
                groupIndex,
                fieldValue,
                handleFieldChange,
                disabled,
                errorMessage
              )}
            </React.Fragment>
          );
        case "revenueInput":
          return (
            <React.Fragment key={fieldKey}>
              {renderRevenueInputField(
                field,
                groupIndex,
                fieldValue.toString(),
                handleFieldChange,
                disabled,
                errorMessage
              )}
            </React.Fragment>
          );
        case "group":
          return (
            <React.Fragment key={fieldKey}>
              {renderGroupField(field as GroupField, groupIndex)}
            </React.Fragment>
          );
        case "dynamicInputPair":
          return (
            <React.Fragment key={fieldKey}>
              {renderDynamicInputPairField(
                field,
                groupIndex,
                groupField.name ?? "",
                disabled,
                validationErrors
              )}
            </React.Fragment>
          );
        case "differentDaycares":
          return (
            <React.Fragment key={fieldKey}>
              {renderDifferentDaycaresField(
                field,
                groupIndex,
                disabled,
                validationErrors
              )}
            </React.Fragment>
          );
        case "component":
          return (
            <React.Fragment key={fieldKey}>
              <field.component {...field.props} taxYear={taxYear} />
            </React.Fragment>
          );
        default:
          return <React.Fragment key={fieldKey}>{null}</React.Fragment>;
      }
    });
  };

  const renderGroupField = (groupField: GroupField, groupIndex: number) => {
    const groupContent = (
      <div className={`field-group ${groupField.layout ?? "column"}`}>
        {groupField.fields.map((f, index) => (
          <div key={f.id || `${f.name}-${index}`} className="field-group-item">
            {renderFields([f], groupIndex)}
          </div>
        ))}
      </div>
    );
    return groupField.isTopLevel ? (
      <div className="top-level-group" key={groupField.id}>
        <h3>{groupField.label}</h3>
        <p className="description">{groupField.description}</p>
        <div>{groupContent}</div>
      </div>
    ) : (
      groupContent
    );
  };

  return (
    <div className="dynamic-group" key={groupField.id}>
      {groups.length > 1 && groupField?.canAddGroup ? (
        <Tabs
          variant="outline"
          value={activeTab}
          onChange={(value: string | null) => setActiveTab(value ?? "0")}
        >
          <Tabs.List>
            {groups.map((_group: any, index: number) => (
              <Tabs.Tab
                key={index}
                value={index.toString()}
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  gap: "0.5rem",
                  alignItems: "center",
                }}
                rightSection={
                  <IconTrash
                    onClick={() => removeGroup(index)}
                    color="#DC373A"
                    style={{ cursor: "pointer" }}
                  />
                }
              >
                {groupField.groupLabel ?? "Business"} {index + 1}
              </Tabs.Tab>
            ))}
            <button
              onClick={addGroup}
              style={{
                border: "none",
                background: "none",
                cursor: "pointer",
              }}
            >
              <IconPlus size={24} color="#000" />
            </button>
          </Tabs.List>
          {groups.map((_group: any, index: number) => (
            <Tabs.Panel key={index} value={index.toString()}>
              <div>
                {renderFields(
                  groupField.groups?.[index]?.fields ||
                    groupField.groups?.[0]?.fields,
                  index
                )}
              </div>
            </Tabs.Panel>
          ))}
        </Tabs>
      ) : (
        <div key={`${groupField.id}`}>
          {renderFields(groupField.groups?.[0]?.fields || [], 0)}
          {groupField.canAddGroup && (
            <Button
              onClick={addGroup}
              style={{
                border: "1px solid #0077CC",
                background: "none",
                cursor: "pointer",
                color: "#0077CC",
              }}
              rightSection={<IconPlus size={24} color="#0077CC" />}
              disabled={disabled}
            >
              {`Add another ${
                groupField.groupLabel?.toLowerCase() ?? "business"
              }`}
            </Button>
          )}
        </div>
      )}
      <FilingStatusModal opened={opened} setOpened={setOpened} />
    </div>
  );
};

export default DynamicGroup;
